package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initUserCodeShareOfAKtorServicePage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/UserCodeShareOfAKtorService.md", FrontMatterElement.Builder { addScalar("title", "User code is 1–4 % of a Ktor service's CPU: a negative result for a bytecode optimiser"); addScalar("searchTitle", "Kotlin bytecode optimiser plugin: profile of a Ktor CIO service, R8 baseline, negative result"); addScalar("date", "2026-09-07"); addScalar("library", "zavarnik"); addScalar("description", "I set out to build a Kotlin/JVM optimiser plugin and measured first. User code owns 1–4 % of CPU and 3–10 % of allocations on a Ktor service, R8 cannot even run as the baseline on this stack, and the plugin was not built. The numbers and the stand."); }))
}

@Page("/blog/user-code-share-of-a-ktor-service")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun UserCodeShareOfAKtorServicePage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The plan was a Gradle plugin with two engines: a K2 IR pass over user code — boxing at ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("suspend")
        }
        org.jetbrains.compose.web.dom.Text(" boundaries, fused collection chains, lazy logging, hoisted constants — and an ASM transform over the whole runtime classpath. The brief put a gate in front of it: measure what share of a real Ktor CIO service's profile user code actually owns, and what R8 with a sensible server configuration already delivers, because those two numbers bound what such a plugin can ever move. This is the result of that gate. The plugin was not built; the stand, the harness and the research document are in ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/zavarnik") {
            org.jetbrains.compose.web.dom.Text("zavarnik")
        }
        org.jetbrains.compose.web.dom.Text(" under ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("bench/")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docs/research/research-optimizer.md")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-stand") }) {
        org.jetbrains.compose.web.dom.Text("The stand")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Service: Ktor 3.5.2 on CIO, kotlinx.serialization 1.11.0, slf4j-simple. Three endpoints: ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("/echo")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("/items")
            }
            org.jetbrains.compose.web.dom.Text(" (JSON CRUD over an in-memory store), ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("/business")
            }
            org.jetbrains.compose.web.dom.Text(" (collection chains, a regex validation, three ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("logger.debug")
            }
            org.jetbrains.compose.web.dom.Text(" templates per request with debug disabled).")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Load: ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("oha")
            }
            org.jetbrains.compose.web.dom.Text(", 64 connections, keep-alive; JVM pinned to cores 0–7, generator to 8–15, ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("-Xms1g -Xmx1g -XX:+UseG1GC")
            }
            org.jetbrains.compose.web.dom.Text(". ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Correction, 11 September 2026:")
            }
            org.jetbrains.compose.web.dom.Text(" the pinning did not take. ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("taskset -pc <pid>")
            }
            org.jetbrains.compose.web.dom.Text(" sets the affinity of a single ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("thread")
            }
            org.jetbrains.compose.web.dom.Text(", so 49 of the JVM's 50 threads kept the full 20-core mask and the service shared every core with its own load generator. Every number in this post was measured that way. Shares by owner are ratios inside one process and do not depend on it; the throughput figures do. Measured again with the pinning fixed in ")
            com.varabyte.kobweb.silk.components.navigation.Link("/blog/three-ktor-engines-under-one-protocol") {
                org.jetbrains.compose.web.dom.Text("three Ktor engines under one protocol")
            }
            org.jetbrains.compose.web.dom.Text(", where the old protocol is kept as a control and reproduces these numbers.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Profiler: async-profiler 4.5, ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("cpu")
            }
            org.jetbrains.compose.web.dom.Text(" for 120 s then ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("alloc")
            }
            org.jetbrains.compose.web.dom.Text(" for 120 s after 60 s of warm-up.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Machine: Ubuntu 24.04 in WSL2, Core Ultra 7 255HX, OpenJDK 25.0.4.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Attribution: every sample is classified twice. ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("Self")
            }
            org.jetbrains.compose.web.dom.Text(" is the leaf frame; ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("owner")
            }
            org.jetbrains.compose.web.dom.Text(" is the first frame from the leaf that is neither JDK nor JVM — whose code asked for this. Owner is the number that matters: it is the ceiling for anything that rewrites that code.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-share") }) {
        org.jetbrains.compose.web.dom.Text("The share")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Endpoint")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("rps")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("p99")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("user CPU, self / owner")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("user alloc, self / owner")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("/echo")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("35 740")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("11.4 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.0 % / 0.0 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.2 % / 0.2 %")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("/items?limit=20")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("38 008")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("8.0 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.8 % / 0.8 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.3 % / 0.3 %")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("/business")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("33 935")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("10.2 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1.1 % / 2.1 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("3.6 % / 9.9 %")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("On the endpoint written to give user code the most to do, user code owns 2.1 % of CPU and 9.9 % of allocated bytes. Three further CPU profiles of the same endpoint, taken while checking a hypothesis about core pinning, gave 1.7–2.0 %. The rest of ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/business")
        }
        org.jetbrains.compose.web.dom.Text(" by owner: CPU — kotlinx 73.9 %, Ktor 15.5 %, stdlib 8.4 %; allocations — Ktor 36.8 %, kotlinx 26.4 %, stdlib 26.8 %.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A stand can be accused of being written for the answer, so the same harness was run inside the container of a service that was not written for it: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/konekt") {
            org.jetbrains.compose.web.dom.Text("konekt")
        }
        org.jetbrains.compose.web.dom.Text(", my own reference project — an eSIM operator's account backend on Ktor CIO with Exposed and Postgres, sixteen of its own modules on the server's classpath as direct dependencies — under its own k6 scenario at a constant arrival rate, one core and 1 GiB. A reference project, not production traffic; what it adds is a database, a serialiser and a framework stack that the stand does not have.")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Rate")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("p95")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("user CPU, self / owner")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("user alloc, self / owner")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("50 rps")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("3.97 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("2.5 % / 4.0 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1.0 % / 3.4 %")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("200 rps")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("2.93 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.5 % / 0.9 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1.3 % / 5.4 %")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("konekt is a database driver, a serialiser and a framework with a thin layer of user code on top; the share is lower than on the stand, not higher. Its biggest allocation owners were stdlib (26.6 %), Ktor (24.8 %), kotlinx (13.6 %) and Exposed (13.5 %) — none of them the territory of a pass over user code.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("where-the-profile-actually-is") }) {
        org.jetbrains.compose.web.dom.Text("Where the profile actually is")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Between a fifth and a third of ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/business")
        }
        org.jetbrains.compose.web.dom.Text(" CPU is the coroutine dispatcher's queue, under ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("LimitedDispatcher")
        }
        org.jetbrains.compose.web.dom.Text(". In the run the tables above come from it was 36 %: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("LockFreeTaskQueue.removeFirstOrNull")
        }
        org.jetbrains.compose.web.dom.Text(" at 30.1 % self plus ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("LockFreeTaskQueueCore.removeFirstOrNull")
        }
        org.jetbrains.compose.web.dom.Text(" at 5.9 %. A repeat two days later, same machine and same harness, put the same family of frames at 21.6 %, with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("LockFreeTaskQueueCore.removeFirstOrNull")
        }
        org.jetbrains.compose.web.dom.Text(" the only one of the two present, at 13.8 %. The share moves with how fast the handlers are — the repeat ran at half the throughput with a p50 of 2.6 ms against 1.2 — and which of the two is cause and which effect is not established here, so the honest form of the number is the range. I took it for an artefact of pinning the JVM to 8 cores while ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("availableProcessors")
        }
        org.jetbrains.compose.web.dom.Text(" saw 20, and measured it three more ways: pinned, pinned with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-XX:ActiveProcessorCount=8")
        }
        org.jetbrains.compose.web.dom.Text(", and unpinned. The queue is the largest Kotlin-side cost in all of them, with the frames shifting between ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("removeFirstOrNull")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("obtainTaskOrDeallocateWorker")
        }
        org.jetbrains.compose.web.dom.Text(". It is a property of CIO under this load — 64 keep-alive connections — not of the harness, and neither an IR pass nor a bytecode peephole can touch it at either end of the range. Netty against CIO under the same protocol is the obvious next measurement; it is not in this post.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Follow-up, 11 September 2026.")
        }
        org.jetbrains.compose.web.dom.Text(" It has been measured: ")
        com.varabyte.kobweb.silk.components.navigation.Link("/blog/three-ktor-engines-under-one-protocol") {
            org.jetbrains.compose.web.dom.Text("three Ktor engines under one protocol")
        }
        org.jetbrains.compose.web.dom.Text(". On the same service under Netty, kotlinx.coroutines owns 8.9 % of CPU against CIO's 56.9 %, so this is the price of the route through ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Dispatchers.IO")
        }
        org.jetbrains.compose.web.dom.Text(" — a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("LimitedDispatcher")
        }
        org.jetbrains.compose.web.dom.Text(" with one queue and up to 64 workers polling it — rather than the price of coroutines; ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Dkotlinx.coroutines.io.parallelism")
        }
        org.jetbrains.compose.web.dom.Text(" set to the core count removes most of it, at the cost of the threads a blocking handler needs. The three configurations named just above were, because of the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("taskset")
        }
        org.jetbrains.compose.web.dom.Text(" mistake, two.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The rest, by size:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The top of the allocation profile is ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("byte[]")
            }
            org.jetbrains.compose.web.dom.Text(" at 18.4 %, ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("String")
            }
            org.jetbrains.compose.web.dom.Text(" 6.8 %, ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Object[]")
            }
            org.jetbrains.compose.web.dom.Text(" 5.9 %: Ktor's I/O buffers and JSON parsing.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("kotlin.reflect.jvm.internal.KClassImpl.toString")
            }
            org.jetbrains.compose.web.dom.Text(" runs on every ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("call.receive<T>()")
            }
            org.jetbrains.compose.web.dom.Text(", from the default body transformations, and costs 1.98 % of all bytes on ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("/business")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Boxing — ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Integer")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Long")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Double")
            }
            org.jetbrains.compose.web.dom.Text(" — is 1.26 % of allocated bytes. The brief's red line for a boxing pass was 3 %.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("What user code does spend, all on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/business")
        }
        org.jetbrains.compose.web.dom.Text(": a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Regex")
        }
        org.jetbrains.compose.web.dom.Text(" constructed inside the handler, 6.45 % of bytes including the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Matcher")
        }
        org.jetbrains.compose.web.dom.Text(" and its arrays; intermediate collections of ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("filter")
        }
        org.jetbrains.compose.web.dom.Text("/")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("map")
        }
        org.jetbrains.compose.web.dom.Text(" chains, about 4.6 %; the three debug templates, 1.57 %. The first two are fixed by moving one line each. Nothing else is above 2 %.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("r8-as-the-baseline") }) {
        org.jetbrains.compose.web.dom.Text("R8 as the baseline")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The brief asked for R8 with a server configuration — no obfuscation, keeps on serialisation, Ktor, coroutines and slf4j, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-assumenosideeffects")
        }
        org.jetbrains.compose.web.dom.Text(" on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Intrinsics.check*")
        }
        org.jetbrains.compose.web.dom.Text(" — as the line a plugin must beat. R8 9.4.17 processes the 26 jars in 26 s, 6503 classes to 5416. The result does not start:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""java.lang.VerifyError: Bad invokespecial instruction: interface method to invoke is not in a direct superinterface
    at kotlinx.coroutines.CompletableDeferred.access${'$'}cancel${'$'}jd
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The mechanism is visible in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("javap")
        }
        org.jetbrains.compose.web.dom.Text(". Kotlin compiles the synthetic accessor as ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("invokespecial InterfaceMethod CompletableDeferred.cancel:()V")
        }
        org.jetbrains.compose.web.dom.Text(", a call to a default method through the current interface. R8 rebinds the reference to the declaring interface, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("invokespecial InterfaceMethod Job.cancel:()V")
        }
        org.jetbrains.compose.web.dom.Text(", and the verifier requires the interface in an ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("invokespecial")
        }
        org.jetbrains.compose.web.dom.Text(" to be the current class or a ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("direct")
        }
        org.jetbrains.compose.web.dom.Text(" superinterface; ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Job")
        }
        org.jetbrains.compose.web.dom.Text(" is reached through ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Deferred")
        }
        org.jetbrains.compose.web.dom.Text(". ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-dontoptimize -dontshrink")
        }
        org.jetbrains.compose.web.dom.Text(" gives the same error, so does ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("--no-desugaring")
        }
        org.jetbrains.compose.web.dom.Text(", so does 9.5.10-dev; keeping coroutines out of the input moves the error into Ktor's ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Routing")
        }
        org.jetbrains.compose.web.dom.Text(". kotlinc had the same bug on its side until 1.5.0 (KT-42753). What synthesises the accessor is Kotlin's compatibility mode, not a flag a project opts into: under ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-jvm-default=enable")
        }
        org.jetbrains.compose.web.dom.Text(" — the default on Kotlin 2.4.10, and what the old ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Xjvm-default=all-compatibility")
        }
        org.jetbrains.compose.web.dom.Text(" selects — an interface gets both the default method and the legacy ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("DefaultImpls")
        }
        org.jetbrains.compose.web.dom.Text(" class, and a separate class cannot reach a default method with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("invokespecial")
        }
        org.jetbrains.compose.web.dom.Text(", so the compiler bridges them with a static ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("access${'$'}…${'$'}jd")
        }
        org.jetbrains.compose.web.dom.Text(" on the interface whose body is exactly the instruction above. Under ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-jvm-default=no-compatibility")
        }
        org.jetbrains.compose.web.dom.Text(", which is what the old ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Xjvm-default=all")
        }
        org.jetbrains.compose.web.dom.Text(" maps to, there is no ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("DefaultImpls")
        }
        org.jetbrains.compose.web.dom.Text(", no accessor and nothing to rebind. The affected set is the default setting, which is how it covers the whole coroutines and Ktor stack. The rule is a classfile rule; DEX has no equivalent, which is presumably why the classfile backend never met it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("What can be measured is R8 over the user jar alone, with the 25 library jars on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("--classpath")
        }
        org.jetbrains.compose.web.dom.Text(". That removes all 56 ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Intrinsics.check*")
        }
        org.jetbrains.compose.web.dom.Text(" calls from user code and cuts its bytecode by 35 %. The effect on the service, same protocol:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Endpoint")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("alloc/req, baseline")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("alloc/req, R8")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("rps A/B median, baseline")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("rps A/B median, R8")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("/echo")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("14.6 KiB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("14.6 KiB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("36 990")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("41 434")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("/items")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("23.7 KiB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("23.2 KiB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("37 439")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("37 143")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("/business")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("33.8 KiB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("34.0 KiB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("27 531")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("28 241")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Allocations per request did not move (within 2 %): null checks do not allocate, and inlining inside 39 classes changed nothing. Throughput was measured separately, without profilers, both variants alternating, three runs each, medians. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/echo")
        }
        org.jetbrains.compose.web.dom.Text(" — where there is no user code and R8 had nothing to change — came out 12 % faster under B. That number is the noise ruler of this stand, and the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/business")
        }
        org.jetbrains.compose.web.dom.Text(" delta of +2.6 % sits inside it.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-the-stand-taught-about-measuring") }) {
        org.jetbrains.compose.web.dom.Text("What the stand taught about measuring")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("A CPU profiler at 1 ms costs throughput: ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("/echo")
            }
            org.jetbrains.compose.web.dom.Text(" served 35 740 rps under it and 44 906 rps in the allocation window of the same run. Profile and throughput are two protocols, not one.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Run-to-run spread on this machine is ±15 % even on the endpoint nothing changed on. WSL2 exposes neither ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("cpufreq")
            }
            org.jetbrains.compose.web.dom.Text(" nor ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("intel_pstate")
            }
            org.jetbrains.compose.web.dom.Text("; frequency belongs to the Windows host. One run per variant is not a measurement.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The first inlining log had zero mentions of user code and looked like a clean negative. It was taken without ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("-XX:+PrintCompilation")
            }
            org.jetbrains.compose.web.dom.Text(", which is what names the compilation roots. With it, five refusals name user methods, all of them \"hot method too big\" — ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Pricing.quote")
            }
            org.jetbrains.compose.web.dom.Text(" at 1827 bytes against C2's ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("FreqInlineSize")
            }
            org.jetbrains.compose.web.dom.Text(" of 325.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("verdict") }) {
        org.jetbrains.compose.web.dom.Text("Verdict")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The gate had two conditions: green if user code owns at least 25 % of allocations, ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("or")
        }
        org.jetbrains.compose.web.dom.Text(" if R8 improves the service by less than 3 % — the second on the assumption that R8 sets the bar and a plugin has room above it. By that letter the result is green: R8 moved nothing. But the reason it moved nothing is not that R8 is weak; it is that there was nothing to move. The ceiling of a pass over user code is the share of the profile user code owns, and that share is 2.1 % of CPU and 9.9 % of allocations on the stand, 1–4 % and 3–5 % on konekt. Removing ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("every")
        }
        org.jetbrains.compose.web.dom.Text(" user allocation would shift alloc/req by a tenth; the brief's own kill criterion — 5 % of throughput or 10 % of allocations with every pass on — is unreachable by construction. The ASM layer reaches the whole process, but what is large in the profile — the dispatcher queue, the I/O buffers, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("KClassImpl.toString")
        }
        org.jetbrains.compose.web.dom.Text(" — is not a peephole. The plugin was not built.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("What survived: a method-size diagnostic (10 of 296 user methods over 325 bytes, three of them refused as hot) now lives as a lint task in the portfolio's build conventions, and the stand stays in zavarnik as the harness for its warm-up measurements.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("re-run-2026-09-08") }) {
        org.jetbrains.compose.web.dom.Text("Re-run 2026-09-08")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The profile was taken again from scratch two days after the numbers above, same machine, same harness. The shares held to within half a point — user code owns 2.5 % of CPU and 10.2 % of allocations on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/business")
        }
        org.jetbrains.compose.web.dom.Text(" against 2.1 % and 9.9 % here — and so did everything about R8: 6503 classes to 5416, the same ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("VerifyError")
        }
        org.jetbrains.compose.web.dom.Text(" at the same method, all 56 ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Intrinsics.check*")
        }
        org.jetbrains.compose.web.dom.Text(" calls gone from the user jar, the same ten methods over 325 bytes with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Pricing.quote")
        }
        org.jetbrains.compose.web.dom.Text(" at 1827. Throughput did not reproduce at all: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/business")
        }
        org.jetbrains.compose.web.dom.Text(" served 16 902 rps against 33 935, which is this post's own point about what the stand can and cannot measure. Corrected in the text: the dispatcher-queue share now carries the range it actually has, and two counts — konekt's modules and the method denominator — were wrong.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("correction-2026-09-11") }) {
        org.jetbrains.compose.web.dom.Text("Correction 2026-09-11")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The paragraph above used to say that any interface compiled with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Xjvm-default=all")
        }
        org.jetbrains.compose.web.dom.Text(" is affected. That is precisely the mode that is not. The issue came back with a reproduction and an explanation pointing at Kotlin's compatibility mode instead, and compiling the same three-interface hierarchy four ways on Kotlin 2.4.10 settles it: the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("access${'$'}…${'$'}jd")
        }
        org.jetbrains.compose.web.dom.Text(" accessor is emitted with no flag at all and under ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-jvm-default=enable")
        }
        org.jetbrains.compose.web.dom.Text(", and is absent under ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-jvm-default=no-compatibility")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-jvm-default=disable")
        }
        org.jetbrains.compose.web.dom.Text(". The shipped ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kotlinx-coroutines-core-jvm-1.11.0.jar")
        }
        org.jetbrains.compose.web.dom.Text(" carries 24 ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("DefaultImpls")
        }
        org.jetbrains.compose.web.dom.Text(" classes, which is the same mode seen from the other side. The correction widens the blast radius rather than narrowing it: it is what you get by default, not what you asked for.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("limitations") }) {
        org.jetbrains.compose.web.dom.Text("Limitations")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("One machine, a mobile CPU in WSL2, with no way to fix the clock. Shares by owner are ratios inside one process and were stable across repeats; throughput figures are not.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Not all of the CPU profile resolves to a name: in the repeat, 15.2 % of ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("/business")
            }
            org.jetbrains.compose.web.dom.Text(" self time is ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("libc.so.6")
            }
            org.jetbrains.compose.web.dom.Text(" with no further attribution. Nothing here rests on it, and it is neither user code nor anything a plugin over user code could reach, but it is a sixth of the profile that this post does not account for.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Allocation bytes are sampled by async-profiler; alloc/req is comparable between variants, not an absolute.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("One real service, one scenario. A service whose handlers do their own arithmetic would score higher; none of mine do.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("R8 was tried at 9.4.17 and 9.5.10-dev on a Kotlin 2.4.10 / coroutines 1.11.0 classpath. It is filed upstream as ")
            com.varabyte.kobweb.silk.components.navigation.Link("https://issuetracker.google.com/issues/558351430") {
                org.jetbrains.compose.web.dom.Text("issue 558351430")
            }
            org.jetbrains.compose.web.dom.Text(", with R8's own input dump attached. Whether it was already known I could not establish, and that link may show you nothing: the tracker is readable only after a sign-in. The ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("javap")
            }
            org.jetbrains.compose.web.dom.Text(" before/after and the reproducer are in ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("bench/profile/")
            }
            org.jetbrains.compose.web.dom.Text(" for anyone who wants to.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/zavarnik") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/zavarnik")
        }
        org.jetbrains.compose.web.dom.Text(" — ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("bench/")
        }
        org.jetbrains.compose.web.dom.Text(" for the service and the harness, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("bench/profile/results/")
        }
        org.jetbrains.compose.web.dom.Text(" for every profile cited, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docs/research/research-optimizer.md")
        }
        org.jetbrains.compose.web.dom.Text(" for the fact tables with their addresses. The first phase of the same repository, the AOT cache, has its own post: ")
        com.varabyte.kobweb.silk.components.navigation.Link("/blog/stale-aot-cache-on-jdk25") {
            org.jetbrains.compose.web.dom.Text("OpenJDK 25.0.0–25.0.3 uses a stale AOT cache without saying so")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
}
